import React from 'react';
import { createPortal } from 'react-dom';
import { ImageOff } from 'lucide-react';
import { resolveImageUrl } from '../../utils/imageUrl';

/**
 * Singleton registry so only ONE ScoreBadge tooltip can be open at a time
 * (across BestMatches, grid view, and list view).
 */
let _activeBadgeId = null;
const _listeners = new Set();
const _setActiveBadge = (id) => {
  _activeBadgeId = id;
  _listeners.forEach((l) => l(id));
};
const useActiveBadge = () => {
  const [activeId, setActiveId] = React.useState(_activeBadgeId);
  React.useEffect(() => {
    _listeners.add(setActiveId);
    return () => _listeners.delete(setActiveId);
  }, []);
  return activeId;
};

/**
 * BestMatches - Horizontal scrolling section showing top 10 best matches
 */
const BestMatches = ({ items = [], onItemClick, cacheBuster }) => {
  if (items.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="best-matches">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-charcoal">Top Matches</span>
        <span className="text-xs text-gray-soft">({items.length})</span>
      </div>

      {/* Horizontal scroll container */}
      <div className="overflow-x-auto -mx-4 px-4 pb-2">
        <div className="flex gap-3" style={{ minWidth: 'min-content' }}>
          {items.map((item) => (
            <BestMatchCard
              key={item.item_id}
              item={item}
              onClick={() => onItemClick(item)}
              cacheBuster={cacheBuster}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const BestMatchCard = ({ item, onClick, cacheBuster }) => {
  const [imageBroken, setImageBroken] = React.useState(false);

  const imageUrl = item.image_url
    ? `${resolveImageUrl(item.image_url)}?v=${cacheBuster}`
    : null;

  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 w-32 bg-white rounded-lg border border-beige overflow-hidden hover:border-sage hover:shadow-md transition-all text-left flex flex-col"
      data-testid={`best-match-${item.item_id}`}
    >
      {/* Image - fixed aspect ratio */}
      <div className="aspect-square bg-cream relative flex-shrink-0">
        {imageUrl && !imageBroken ? (
          <img
            src={imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={() => setImageBroken(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageOff size={24} className="text-gray-light" />
          </div>
        )}

        {/* Relevance score badge */}
        {item.relevance_score > 0 && (
          <div className="absolute top-1.5 right-1.5">
            <ScoreBadge
              badgeId={`best-${item.item_id}`}
              score={item.relevance_score}
              breakdown={item.score_breakdown}
            />
          </div>
        )}
      </div>

      {/* Info - name (up to 2 lines) + brand */}
      <div className="p-2">
        <p className="text-xs font-medium text-charcoal line-clamp-2 leading-tight">
          {item.name}
        </p>
        {item.brand && (
          <p className="text-[10px] text-gray-soft mt-0.5 truncate">
            {item.brand}
          </p>
        )}
      </div>
    </button>
  );
};

/**
 * Self-contained Score Badge with click-to-reveal breakdown.
 * - Tooltip rendered via Portal (escapes overflow:hidden parents)
 * - Singleton open state: only ONE tooltip visible across the app
 * - Closes on outside click and on scroll (anywhere)
 */
export const ScoreBadge = ({ badgeId, score, breakdown }) => {
  const buttonRef = React.useRef(null);
  const tooltipRef = React.useRef(null);
  const activeId = useActiveBadge();
  const isOpen = activeId === badgeId;
  const [tooltipPos, setTooltipPos] = React.useState(null);

  const formatBreakdown = () => {
    if (!breakdown) return null;
    const parts = [];
    if (breakdown.name) parts.push({ label: 'Name', value: breakdown.name });
    if (breakdown.brand) parts.push({ label: 'Brand', value: breakdown.brand });
    if (breakdown.sentiment) parts.push({ label: 'Sentiment', value: breakdown.sentiment });
    if (breakdown.category) parts.push({ label: 'Category', value: breakdown.category });
    if (breakdown.tags) parts.push({ label: 'Tags', value: breakdown.tags });
    if (breakdown.expanded) parts.push({ label: 'Related', value: breakdown.expanded });
    return parts;
  };

  const breakdownParts = formatBreakdown();

  const handleToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    _setActiveBadge(isOpen ? null : badgeId);
  };

  // Compute tooltip position when opened
  React.useLayoutEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  // Close on outside click + any scroll while open
  React.useEffect(() => {
    if (!isOpen) return;
    const close = (e) => {
      if (e && tooltipRef.current && tooltipRef.current.contains(e.target)) return;
      if (e && buttonRef.current && buttonRef.current.contains(e.target)) return;
      _setActiveBadge(null);
    };
    const closeImmediate = () => _setActiveBadge(null);
    document.addEventListener('click', close);
    window.addEventListener('scroll', closeImmediate, true);
    window.addEventListener('resize', closeImmediate);
    return () => {
      document.removeEventListener('click', close);
      window.removeEventListener('scroll', closeImmediate, true);
      window.removeEventListener('resize', closeImmediate);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="bg-sage/90 text-white text-[11px] font-medium px-2 py-1 rounded hover:bg-sage transition-colors"
        title="Click to see score breakdown"
      >
        {score}%
      </button>

      {isOpen && breakdownParts && breakdownParts.length > 0 && tooltipPos && createPortal(
        <div
          ref={tooltipRef}
          className="fixed z-[9999] bg-charcoal text-white text-[11px] px-3 py-2 rounded-lg shadow-xl min-w-[160px]"
          style={{ top: `${tooltipPos.top}px`, right: `${tooltipPos.right}px` }}
        >
          {/* Arrow pointing up to the badge */}
          <div
            className="absolute w-0 h-0"
            style={{
              top: '-6px',
              right: '8px',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid #1f2937',
            }}
          />
          <div className="font-medium mb-1.5 text-white/90 border-b border-white/20 pb-1">Score Breakdown</div>
          {breakdownParts.map((part, i) => (
            <div key={i} className="flex justify-between gap-4 py-0.5">
              <span className="text-white/70">{part.label}</span>
              <span className="text-green-400 font-medium">+{part.value}</span>
            </div>
          ))}
          <div className="flex justify-between gap-4 border-t border-white/20 mt-1.5 pt-1.5">
            <span className="font-medium">Total</span>
            <span className="font-medium text-green-400">{score}%</span>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default BestMatches;
